import { useAccount, useMsal } from '@azure/msal-react';
import { cva, type VariantProps } from 'class-variance-authority';

import AppLogo from '@/assets/icons/app-logo.svg?react';
import Logo from '@/assets/icons/logo.svg?react';

import { T } from '@/components/i18n/message.i18n.component';
import { cn } from '@/lib/utils';
import { Help } from './components/help.navbar';
import { Notification } from './components/notification.navbar';
import { UserInfo } from './components/user-info.navbar';
import { navbarTranslations } from './navbar.messages';

const variants = cva('bg-dark flex flex-row items-center justify-between fixed top-0 left-0 right-0 z-10', {
  variants: {
    variant: {
      default: 'bg-dark text-white-text',
    },
    size: {
      default: 'px-18 h-16 xs:px-0 xs:h-12 w-full',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
  },
});

interface NavbarProps extends React.HTMLAttributes<HTMLDivElement>, Omit<VariantProps<typeof variants>, ''> {}

const Navbar = ({ variant, className, ...props }: NavbarProps) => {
  const { instance, accounts } = useMsal();
  const account = useAccount(accounts[0]);

  const handleLogout = () => {
    instance.logoutRedirect({
      account: account,
      postLogoutRedirectUri: '/login',
      onRedirectNavigate: () => {
        return false;
      },
    });
  };

  const environment = (env: 'dev' | 'test' | 'prod') => {
    if (env === 'dev') {
      return (
        <p className="typo-secondary font-semibold uppercase text-system-error whitespace-nowrap">
          <T {...navbarTranslations.developmentEnvironment} />
        </p>
      );
    }
    if (env === 'test') {
      return (
        <p className="typo-secondary font-semibold uppercase text-system-warning whitespace-nowrap">
          <T {...navbarTranslations.testEnvironment} />
        </p>
      );
    }
    return undefined;
  };

  return (
    <div className={cn(variants({ variant, className }))} {...props}>
      <div className="flex flex-row items-center">
        <Logo
          role="img"
          aria-label="Siemens logo"
          data-testid="navbar-siemens-logo"
          className="h-8 flex mr-6 xs:mr-2 xs:h-4 xs:ml-2"
        />
        <AppLogo aria-label="Application Logo" role="img" data-testid="navbar-app-logo" className="mr-2" />
        <p className="typo-primary-500" data-testid="navbar-app-name">
          <T {...navbarTranslations.appName} />
        </p>
      </div>
      <div className="flex flex-row items-center gap-x-4">
        <div className="flex flex-row items-center gap-x-5">
          {environment(import.meta.env.VITE_ENV) && (
            <div
              data-testid="navbar-environment"
              style={{
                // has to be hardcoded due to a design mismatch and tailwind not supporting alpha values
                backgroundColor: 'rgba(155, 155, 155, 0.2)',
              }}
              className="px-3 py-1 rounded-full"
            >
              {environment(import.meta.env.VITE_ENV)}
            </div>
          )}
          <Notification />
          <Help />
        </div>
        <UserInfo account={account} onLogout={handleLogout} />
      </div>
    </div>
  );
};

export default Navbar;
